import React, { useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { paymentService } from '../components/services/paymentServices';
import { usePayment } from '../components/hooks/hooks-e-commerce/usePayment';
import { useAddressUpdate } from '../components/hooks/hooks-e-commerce/useAddressUpdate';
import { showToast } from '../contexts/ToastNotification';

import PayPalButton from '../components/modules/Buttons/PayPalButton';
import BonificoPagamento from '../components/modules/Buttons/BonificoPagamento';
import './ordine-pagamento.scss';

// Costanti
const PROVINCE_ABRUZZO = ['AQ', 'CH', 'PE', 'TE'];
const IVA_PERCENTUALE = 22;
const REGEX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REGEX_CAP = /^\d{5}$/;
const REGEX_PROVINCIA = /^[A-Z]{2}$/;
const REGEX_TELEFONO = /^[+\d\s\-()]{10,}$/;
const REGEX_CODICE_FISCALE = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i;
const REGEX_PARTITA_IVA = /^\d{11}$/;

// Funzioni helper fuori dal componente
const getProvincia = (indirizzo) => {
    if (!indirizzo) return null;
    if (typeof indirizzo === 'object') return indirizzo.provincia;
    if (typeof indirizzo === 'string') {
        try {
            return JSON.parse(indirizzo).provincia;
        } catch {
            return null;
        }
    }
    return null;
};

const formatEuro = (value) => {
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(num) ? '0.00' : num.toFixed(2);
};

const validateAddressForm = (addressData, isBilling = false) => {
    const errors = {};
    
    if (!addressData.nome?.trim()) errors.nome = 'Nome obbligatorio';
    if (!addressData.cognome?.trim()) errors.cognome = 'Cognome obbligatorio';
    if (!addressData.indirizzo?.trim()) errors.indirizzo = 'Indirizzo obbligatorio';
    if (!addressData.citta?.trim()) errors.citta = 'Città obbligatoria';
    if (!addressData.provincia?.trim()) errors.provincia = 'Provincia obbligatoria (es. PE, RM, MI)';
    if (!addressData.cap?.trim()) errors.cap = 'CAP obbligatorio';
    if (!addressData.telefono?.trim()) errors.telefono = 'Telefono obbligatorio';
    
    if (!addressData.email?.trim()) {
        errors.email = 'Email obbligatoria';
    } else if (!REGEX_EMAIL.test(addressData.email)) {
        errors.email = 'Email non valida';
    }
    
    if (addressData.cap && !REGEX_CAP.test(addressData.cap)) {
        errors.cap = 'CAP deve essere di 5 cifre';
    }
    
    if (addressData.provincia && !REGEX_PROVINCIA.test(addressData.provincia.toUpperCase())) {
        errors.provincia = 'Provincia deve essere 2 lettere (es. PE, RM, MI)';
    }
    
    if (addressData.telefono && !REGEX_TELEFONO.test(addressData.telefono)) {
        errors.telefono = 'Telefono non valido';
    }
    
    if (isBilling && addressData.codFiscale && !REGEX_CODICE_FISCALE.test(addressData.codFiscale)) {
        errors.codFiscale = 'Codice Fiscale non valido';
    }
    
    if (isBilling && addressData.partitaIva && !REGEX_PARTITA_IVA.test(addressData.partitaIva)) {
        errors.partitaIva = 'Partita IVA deve essere 11 cifre';
    }
    
    return { isValid: Object.keys(errors).length === 0, errors };
};

function OrdinePagamento() {
    const location = useLocation();
    
    const rawOrder = location.state?.order;
    const numeroOrdine = location.state?.numeroOrdine;
    const indirizzoSpedizione = location.state?.indirizzo_spedizione;
    const [indirizzoFatturazione, setIndirizzoFatturazione] = useState(location.state?.indirizzo_fatturazione || null);
    
    // TUTTI GLI HOOK ALL'INIZIO
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [isEditingShipping, setIsEditingShipping] = useState(false);
    const [isEditingBilling, setIsEditingBilling] = useState(false);
    const [editedShipping, setEditedShipping] = useState(indirizzoSpedizione || {});
    const [editedBilling, setEditedBilling] = useState(indirizzoFatturazione || {});
    const [currentShippingAddress, setCurrentShippingAddress] = useState(indirizzoSpedizione);
    
    const { updateDiscount, saveOrderToSession, prepareDiscountedOrder, confirmPayment } = usePayment();
    const { updateShippingAddress, updateBillingAddress } = useAddressUpdate();

    const [orderData, setOrderData] = useState(location.state?.order || null);
    
    
    // useCallback dichiarati PRIMA dei return condizionali
    const handleShippingChange = useCallback((e) => {
        const { name, value } = e.target;
        setEditedShipping(prev => ({ ...prev, [name]: value }));
    }, []);
    
    const handleBillingChange = useCallback((e) => {
        const { name, value } = e.target;
        setEditedBilling(prev => ({ ...prev, [name]: value }));
    }, []);
    
    const order = useMemo(() => {
        if (!rawOrder) return null;
        return {
            ...rawOrder,
            subtotale: parseFloat(rawOrder.subtotale) || 0,
            costo_spedizione: parseFloat(rawOrder.costo_spedizione) || 0,
            spese_imballaggio: parseFloat(rawOrder.spese_imballaggio) || 0,
            importo_iva: parseFloat(rawOrder.importo_iva) || 0,
            importo_totale: parseFloat(rawOrder.importo_totale) || 0,
            sconto_spedizione: parseFloat(rawOrder.sconto_spedizione) || 0,
            numero_bottiglie: parseInt(rawOrder.numero_bottiglie, 10) || 0
        };
    }, [rawOrder]);
    
    const shippingDiscount = useMemo(() => {
        const provincia = getProvincia(currentShippingAddress);
        const isAbruzzo = PROVINCE_ABRUZZO.includes(provincia);
        
        if (isAbruzzo && order?.costo_spedizione) {
            const discountedShipping = order.costo_spedizione;
            return {
                isAbruzzo: true,
                discount: discountedShipping,
                originalShipping: discountedShipping * 2,
                discountedShipping: discountedShipping,
                discountPercentage: 50
            };
        }
        
        return {
            isAbruzzo: false,
            discount: 0,
            originalShipping: order?.costo_spedizione || 0,
            discountedShipping: order?.costo_spedizione || 0,
            discountPercentage: 0
        };
    }, [currentShippingAddress, order?.costo_spedizione]);
    
    const updatedTotals = useMemo(() => {
        const discountedTotal = order?.importo_totale || 0;
        return {
            originalTotal: discountedTotal + shippingDiscount.discount,
            discountedTotal: discountedTotal,
            discountAmount: shippingDiscount.discount,
            shippingCost: shippingDiscount.discountedShipping
        };
    }, [order?.importo_totale, shippingDiscount]);
    
    const ivaCalcoli = useMemo(() => {
        const imponibileProdotti = order?.subtotale || 0;
        const imponibileSpedizione = updatedTotals.shippingCost;
        const imponibileImballaggio = order?.spese_imballaggio || 0;
        
        const ivaProdotti = imponibileProdotti * (IVA_PERCENTUALE / 100);
        const ivaSpedizione = imponibileSpedizione * (IVA_PERCENTUALE / 100);
        const ivaImballaggio = imponibileImballaggio * (IVA_PERCENTUALE / 100);
        
        return {
            ivaProdotti,
            ivaSpedizione,
            ivaImballaggio,
            totaleIva: ivaProdotti + ivaSpedizione + ivaImballaggio
        };
    }, [order?.subtotale, order?.spese_imballaggio, updatedTotals.shippingCost]);
    
    // DOPO TUTTI GLI HOOK, POI I return condizionali
    if (!order || !indirizzoSpedizione) {
        return (
            <div className="pagamento-error">
                <h2>❌ Dati mancanti</h2>
                <button onClick={() => window.history.back()}>Indietro</button>
            </div>
        );
    }
    
    // ✅ Funzione per aprire la modifica spedizione (resetta città, provincia, CAP)
    const openShippingEdit = () => {
        setEditedShipping({
            ...indirizzoSpedizione,
            citta: '',
            provincia: '',
            cap: ''
        });
        setIsEditingShipping(true);
    };

    // ✅ Funzione per aprire la modifica fatturazione (resetta città, provincia, CAP)
    const openBillingEdit = () => {
    // Se esiste un indirizzo di fatturazione, usalo, altrimenti crea un oggetto vuoto
        const baseAddress = indirizzoFatturazione || {
            nome: '',
            cognome: '',
            azienda: '',
            indirizzo: '',
            indirizzo2: '',
            citta: '',
            provincia: '',
            cap: '',
            nazione: 'Italia',
            telefono: '',
            email: '',
            codFiscale: '',
            partitaIva: '',
        };
        
        setEditedBilling({
            ...baseAddress,
            // Resetta solo città, provincia, CAP per indirizzi esistenti
            ...(indirizzoFatturazione ? { citta: '', provincia: '', cap: '' } : {})
        });
        setIsEditingBilling(true);
    };
    
    // Funzioni di gestione pagamento
    const handleStripePayment = async () => {
        setIsProcessing(true);
        const updatedOrder = prepareDiscountedOrder(order, shippingDiscount, updatedTotals, ivaCalcoli.totaleIva);
        
        try {
            await updateDiscount(order.numero_ordine, {
                scontoSpedizione: updatedOrder.sconto_spedizione,
                scontoTipo: updatedOrder.sconto_tipo,
                importoTotale: updatedOrder.importo_totale,
                costoSpedizione: updatedOrder.costo_spedizione,
                importoIva: updatedOrder.importo_iva
            });
            
            saveOrderToSession(updatedOrder, order.numero_ordine, shippingDiscount.discount);

              //  salva anche con metodo pagamento
            sessionStorage.setItem('orderData', JSON.stringify({
                order: updatedOrder,
                paymentMethod: 'card',
                success: true
            }));

                // SALVA ANCHE L'ORDINE PER LA CONFERMA POST-PAGAMENTO
            sessionStorage.setItem('pendingOrderConfirmation', JSON.stringify({
                orderId: order.numero_ordine,
                discountData: {
                    scontoSpedizione: updatedOrder.sconto_spedizione,
                    scontoTipo: updatedOrder.sconto_tipo,
                    importoTotale: updatedOrder.importo_totale,
                    costoSpedizione: updatedOrder.costo_spedizione,
                    importoIva: updatedOrder.importo_iva
                }
            }));
            
            const { url } = await paymentService.createCheckoutSession(
                updatedOrder,
                shippingDiscount.discount,
                shippingDiscount.isAbruzzo ? 'abruzzo_50' : null
            );
            
            if (url) window.location.href = url;
        } catch (error) {
            console.error('Errore Stripe:', error);
            alert('Errore durante il pagamento');
        } finally {
            setIsProcessing(false);
        }
    };
    
    const handlePayPalSuccess = async (paypalOrderData) => {
        const updatedOrder = prepareDiscountedOrder(order, shippingDiscount, updatedTotals, ivaCalcoli.totaleIva);
        
        try {
            await confirmPayment(
                order.numero_ordine, 
                paypalOrderData.id, 
                'paypal',
                {
                    scontoSpedizione: updatedOrder.sconto_spedizione,
                    scontoTipo: updatedOrder.sconto_tipo,
                    importoTotale: updatedOrder.importo_totale,
                    costoSpedizione: updatedOrder.costo_spedizione,
                    importoIva: updatedOrder.importo_iva
                }
            );
            saveOrderToSession(updatedOrder, order.numero_ordine, shippingDiscount.discount);
            window.location.href = '/negozioonline/checkout/ordine-confermato';
        } catch (error) {
            console.error('❌ Errore PayPal:', error);
        }
    };
    
    const handlePayPalError = (err) => {
        console.error('❌ Errore PayPal:', err);
        alert('Errore durante il pagamento con PayPal');
    };
    
    const handleBonificoSuccess = (bonificoData) => {
        const updatedOrder = {
            ...order,
            costo_spedizione: updatedTotals.shippingCost,
            importo_totale: updatedTotals.discountedTotal,
            sconto_spedizione: shippingDiscount.discount,
            sconto_applicato: shippingDiscount.isAbruzzo ? 'abruzzo_50' : null
        };
        
        sessionStorage.setItem('orderData', JSON.stringify({
            order: updatedOrder,
            paymentMethod: 'bonifico',
            success: true,
            bankDetails: bonificoData
        }));
        
        window.location.href = '/negozioonline/checkout/ordine-confermato';
    };
    
    // ✅ Salva modifiche spedizione con validazione
    const saveShippingChanges = async () => {
        const { isValid, errors } = validateAddressForm(editedShipping, false);
        if (!isValid) {
            const errorMessages = Object.values(errors).join('\n');
            showToast(errorMessages, 'error', 5000);
            return;
        }

        console.log('🔍 Ordine passato a updateShippingAddress:', {
            numero_bottiglie: order.numero_bottiglie,
            subtotale: order.subtotale,
            costo_spedizione: order.costo_spedizione
        });
        
        setIsEditingShipping(false);
        setIsProcessing(true);
        
        try {
            // ✅ PASSA isAddressModified = true!
            const result = await updateShippingAddress(
                order.numero_ordine, 
                editedShipping, 
                order,
                true  // ← isAddressModified = true!
            );
            
            setCurrentShippingAddress(editedShipping);
            order.costo_spedizione = result.order.costo_spedizione;
            order.importo_totale = result.order.importo_totale;
            order.sconto_spedizione = result.order.sconto_spedizione;
            order.sconto_tipo = result.order.sconto_tipo;
            
            // ✅ AGGIORNA ANCHE L'INDIRIZZO DI FATTURAZIONE (se non fornito)
            if (!indirizzoFatturazione) {
                setIndirizzoFatturazione(editedShipping);
                if (order) {
                    order.indirizzo_fatturazione = editedShipping;
                }
            }
            
            showToast('Indirizzo di spedizione aggiornato con successo!', 'success');
        } catch (error) {
            console.error('❌ Errore:', error);
            showToast('Errore durante l\'aggiornamento dell\'indirizzo', 'error');
        } finally {
            setIsProcessing(false);
        }
    };
    
    // ✅ Salva modifiche fatturazione con validazione
    const saveBillingChanges = async () => {
        const { isValid, errors } = validateAddressForm(editedBilling, true);
        if (!isValid) {
            const errorMessages = Object.values(errors).join('\n');
            showToast(errorMessages, 'error', 5000);
            return;
        }
        
        setIsEditingBilling(false);
        setIsProcessing(true);
        
        try {
            const result = await updateBillingAddress(orderData?.numero_ordine, editedBilling);
            
            // ✅ AGGIORNA orderData (esiste già!)
            if (result?.order) {
                setOrderData(prev => ({
                    ...prev,
                    ...result.order,
                    indirizzo_fatturazione: editedBilling
                }));
            }
            
            // ✅ AGGIORNA indirizzoFatturazione (esiste già!)
            setIndirizzoFatturazione(editedBilling);
            
            // ✅ AGGIORNA anche l'oggetto order (se usato)
            if (orderData) {
                orderData.indirizzo_fatturazione = editedBilling;
            }
            
            showToast('Indirizzo di fatturazione aggiornato con successo!', 'success');
        } catch (error) {
            console.error('❌ Errore:', error);
            showToast('Errore durante l\'aggiornamento dell\'indirizzo di fatturazione', 'error');
        } finally {
            setIsProcessing(false);
        }
    };
    
    const cancelShippingChanges = () => {
        setEditedShipping(indirizzoSpedizione);
        setIsEditingShipping(false);
    };
    
    const cancelBillingChanges = () => {
        setEditedBilling(indirizzoFatturazione);
        setIsEditingBilling(false);
    };
    
    const renderAddressFields = (address, isBilling = false) => {
        const data = isBilling ? editedBilling : editedShipping;
        const handleChange = isBilling ? handleBillingChange : handleShippingChange;
        
        return (
            <div className="address-edit-form">
                <div className="form-row">
                    <div className="form-group">
                        <label>Nome *</label>
                        <input type="text" name="nome" value={data.nome || ''} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                        <label>Cognome *</label>
                        <input type="text" name="cognome" value={data.cognome || ''} onChange={handleChange} />
                    </div>
                </div>
                
                <div className="form-group">
                    <label>Azienda</label>
                    <input type="text" name="azienda" value={data.azienda || ''} onChange={handleChange} />
                </div>
                
                <div className="form-group">
                    <label>Indirizzo *</label>
                    <input type="text" name="indirizzo" value={data.indirizzo || ''} onChange={handleChange} placeholder="Via, Piazza, numero civico" />
                </div>
                
                <div className="form-row">
                    <div className="form-group">
                        <label>Città *</label>
                        <input type="text" name="citta" value={data.citta || ''} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                        <label>Provincia *</label>
                        <input 
                            type="text" 
                            name="provincia" 
                            value={data.provincia || ''} 
                            onChange={(e) => handleChange({ target: { name: 'provincia', value: e.target.value.toUpperCase() } })} 
                            maxLength="2" 
                            placeholder="es. PE" 
                        />
                    </div>
                    <div className="form-group">
                        <label>CAP *</label>
                        <input type="text" name="cap" value={data.cap || ''} onChange={handleChange} maxLength="5" placeholder="es. 65100" />
                    </div>
                </div>
                
                <div className="form-row">
                    <div className="form-group">
                        <label>Nazione</label>
                        <input type="text" name="nazione" value={data.nazione || 'Italia'} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                        <label>Telefono *</label>
                        <input type="tel" name="telefono" value={data.telefono || ''} onChange={handleChange} />
                    </div>
                </div>
                
                <div className="form-group">
                    <label>Email *</label>
                    <input type="email" name="email" value={data.email || ''} onChange={handleChange} />
                </div>
                
                {isBilling && (
                    <div className="fiscal-fields">
                        <h4>Dati Fiscali</h4>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Codice Fiscale</label>
                                <input type="text" name="codFiscale" value={data.codFiscale || ''} onChange={handleChange} maxLength="16" />
                            </div>
                            <div className="form-group">
                                <label>Partita IVA</label>
                                <input type="text" name="partitaIva" value={data.partitaIva || ''} onChange={handleChange} maxLength="11" />
                            </div>
                        </div>
                    </div>
                )}
                
                <div className="edit-actions">
                    <button className="save-btn" onClick={isBilling ? saveBillingChanges : saveShippingChanges}>💾 Salva modifiche</button>
                    <button className="cancel-btn" onClick={isBilling ? cancelBillingChanges : cancelShippingChanges}>❌ Annulla</button>
                </div>
            </div>
        );
    };
    
    const renderAddressDetails = (address, isBilling = false) => {
        if (!address) return <p>Indirizzo non disponibile</p>;
        
        return (
            <div className="address-card">
                <p><strong>{address.nome} {address.cognome}</strong></p>
                {address.azienda && <p>{address.azienda}</p>}
                <p>{address.indirizzo}</p>
                {address.indirizzo2 && <p>{address.indirizzo2}</p>}
                <p>{address.cap} {address.citta} ({address.provincia})</p>
                <p>{address.nazione}</p>
                <p>📞 {address.telefono}</p>
                <p>✉️ {address.email}</p>
                
                {isBilling && (address.codFiscale || address.partitaIva) && (
                    <div className="fiscal-data">
                        <h4>Dati Fiscali</h4>
                        {address.codFiscale && <p>Codice Fiscale: {address.codFiscale}</p>}
                        {address.partitaIva && <p>Partita IVA: {address.partitaIva}</p>}
                    </div>
                )}
            </div>
        );
    };
    
    return (
        <div className="pagamento-container">
            <h1>Pagamento</h1>
            
            <div className="order-summary">
                <h2>Riepilogo Ordine</h2>
                <p><strong>Numero ordine:</strong> {numeroOrdine || order.numero_ordine}</p>
                <p><strong>Data:</strong> {new Date().toLocaleDateString('it-IT')}</p>
                <p><strong>Stato:</strong> {order.stato || 'in_attesa'}</p>
            </div>
            
            {/* INDIRIZZO SPEDIZIONE */}
            <div className="address-section">
                <div className="section-header">
                    <h3>📦 Indirizzo di Spedizione</h3>
                    {!isEditingShipping && (
                        <button className="edit-btn" onClick={openShippingEdit}>
                            ✏️ Modifica
                        </button>
                    )}
                </div>
                {isEditingShipping ? renderAddressFields(indirizzoSpedizione, false) : renderAddressDetails(currentShippingAddress, false)}
            </div>
            
            {/* INDIRIZZO FATTURAZIONE */}
            <div className="address-section">
                <div className="section-header">
                    <h3>📄 Indirizzo di Fatturazione</h3>
                    {!isEditingBilling && (
                        <button 
                            className={`edit-btn ${!indirizzoFatturazione ? 'add-btn' : ''}`} 
                            onClick={openBillingEdit}  // ← USA LA FUNZIONE!
                        >
                            {indirizzoFatturazione ? '✏️ Modifica' : '➕ Aggiungi'}
                        </button>
                    )}
                </div>
                
                {isEditingBilling ? (
                    renderAddressFields(indirizzoFatturazione || {}, true)
                ) : indirizzoFatturazione ? (
                    renderAddressDetails(indirizzoFatturazione, true)
                ) : (
                    <div className="address-empty">
                        <p>📝 Nessun indirizzo di fatturazione fornito</p>
                        <p className="address-hint">Clicca su "Aggiungi" per inserire un indirizzo di fatturazione</p>
                    </div>
                )}
            </div>
            
            {/* DETTAGLI ORDINE */}
            <div className="order-details">
                <h3>💰 Dettagli Ordine</h3>
                <div className="details-row"><span>Prodotti:</span><span>€ {formatEuro(order.subtotale)}</span></div>
                <div className="details-row details-sub"><span>‣ di cui IVA (22%):</span><span>€ {formatEuro(ivaCalcoli.ivaProdotti)}</span></div>
                
                {order.spese_imballaggio > 0 && (
                    <>
                        <div className="details-row"><span>📦 Imballaggio:</span><span>€ {formatEuro(order.spese_imballaggio)}</span></div>
                        <div className="details-row details-sub"><span>‣ di cui IVA (22%):</span><span>€ {formatEuro(ivaCalcoli.ivaImballaggio)}</span></div>
                    </>
                )}
                
                <div className="details-row">
                    <span>🚚 Spedizione:</span>
                    {shippingDiscount.isAbruzzo ? (
                        <div className="shipping-discount">
                            <span className="original-price">€ {formatEuro(shippingDiscount.originalShipping)}</span>
                            <span className="discounted-price">€ {formatEuro(shippingDiscount.discountedShipping)}</span>
                            <span className="discount-badge">-50% Abruzzo</span>
                        </div>
                    ) : <span>€ {formatEuro(order.costo_spedizione)}</span>}
                </div>
                
                {shippingDiscount.isAbruzzo && (
                    <div className="details-row discount-row">
                        <span>Sconto spedizione Abruzzo:</span>
                        <span className="discount-amount">- € {formatEuro(shippingDiscount.discount)}</span>
                    </div>
                )}
                
                <div className="details-row details-sub"><span>‣ di cui IVA (22%):</span><span>€ {formatEuro(ivaCalcoli.ivaSpedizione)}</span></div>
                
                <div className="details-row total">
                    <span><strong>Totale ordine:</strong></span>
                    {shippingDiscount.isAbruzzo ? (
                        <div className="total-with-discount">
                            <span className="original-total">€ {formatEuro(updatedTotals.originalTotal)}</span>
                            <span className="discounted-total">€ {formatEuro(updatedTotals.discountedTotal)}</span>
                        </div>
                    ) : <span><strong>€ {formatEuro(order.importo_totale)}</strong></span>}
                </div>
                <div className="details-row"><span>📊 Totale IVA (22%):</span><span>€ {formatEuro(ivaCalcoli.totaleIva)}</span></div>
            </div>
            
            {/* SELEZIONE METODO DI PAGAMENTO */}
            <div className="payment-methods">
                <h3>💳 Scegli metodo di pagamento</h3>
                <div className="payment-options">
                    <label className="payment-option">
                        <input type="radio" name="payment" value="card" checked={paymentMethod === 'card'} onChange={(e) => setPaymentMethod(e.target.value)} />
                        <span>💳 Carta di Credito/Debito (Stripe)</span>
                    </label>
                    <label className="payment-option">
                        <input type="radio" name="payment" value="paypal" checked={paymentMethod === 'paypal'} onChange={(e) => setPaymentMethod(e.target.value)} />
                        <span>🅿️ PayPal</span>
                    </label>
                    <label className="payment-option">
                        <input type="radio" name="payment" value="bonifico" checked={paymentMethod === 'bonifico'} onChange={(e) => setPaymentMethod(e.target.value)} />
                        <span>🏦 Bonifico Bancario</span>
                    </label>
                </div>
            </div>
            
            {/* BOTTONE PAGA */}
            {paymentMethod === 'card' ? (
                <button className="pay-button" onClick={handleStripePayment} disabled={isProcessing}>
                    {isProcessing ? '⏳ Elaborazione...' : `Paga con Carta € ${formatEuro(updatedTotals.discountedTotal)}`}
                </button>
            ) : paymentMethod === 'paypal' ? (
                <PayPalButton 
                    orderData={{ ...order, importo_totale: updatedTotals.discountedTotal, costo_spedizione: updatedTotals.shippingCost }} 
                    onSuccess={handlePayPalSuccess} 
                    onError={handlePayPalError} 
                />
            ) : (
                <BonificoPagamento 
                    orderData={{ ...order, importo_totale: updatedTotals.discountedTotal, costo_spedizione: updatedTotals.shippingCost }} 
                    onSuccess={handleBonificoSuccess} 
                />
            )}
        </div>
    );
}

export default OrdinePagamento;
